import { Button, Modal, Group, Stack } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { primaryColor } from '../../../../constants/theme';
import { useMutation } from '@tanstack/react-query';
import { useApp } from '../../../../context/AppContext';
import { BookingResponseModel } from '../../../../types/BookingModels';
import { ErrorModal } from '../error/ErrorModal';
import { BookingPreview } from './BookingPreview';
import { hideBooking } from '../../../../services/BookingService';

interface RemoveBookingProps {
  refreshBookings: () => void;
  profileId: string;
  groupedBookings: BookingResponseModel[];
}

export const RemoveBookingButton: React.FC<RemoveBookingProps> = ({
  refreshBookings,
  profileId,
  groupedBookings,
}) => {
  const booking = groupedBookings[0];
  const { token } = useApp();
  const [opened, setOpened] = useState(false);
  const isProfessional = profileId === booking?.professionalId;

  const {
    mutate: removeBookingMutation,
    isPending,
    isError,
    reset,
  } = useMutation({
    mutationFn: async () => {
      await hideBooking(
        {
          bookingIds: groupedBookings.map((b) => b.id),
        },
        token || ''
      );
    },
    onSuccess: () => {
      refreshBookings();
      setOpened(false);
    },
    onError: (error) => {
      console.error('Error removing booking:', error);
    },
  });

  if (booking.accepted || !booking.isCancelled) return null;

  return (
    <>
      <ErrorModal
        isInError={isError}
        errorMessage="There was an error removing the booking. Please try again."
        dismissAction={() => reset()}
        retryAction={() => removeBookingMutation()}
        isLoading={isPending}
      />
      <Button
        radius="md"
        variant="outline"
        color={primaryColor}
        leftSection={<IconX size={16} />}
        onClick={() => setOpened(true)}
        data-testid={`remove-button-${booking.id}`}
      >
        Remove
      </Button>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        withCloseButton={false}
      >
        <Stack gap={10}>
          <Stack justify="center" align="center" gap={10}>
            <BookingPreview
              isProfessional={isProfessional}
              title="Remove Booking?"
              groupedBookings={groupedBookings}
              bottomText="Booking will be hidden from your bookings page"
              titleTestId="remove-modal-title"
              textTestId="remove-modal-text"
            />
          </Stack>
          <Group gap={10} justify="center">
            <Button
              variant="outline"
              w="48%"
              color={primaryColor}
              onClick={() => setOpened(false)}
              data-testid="remove-modal-cancel-cta"
            >
              Cancel
            </Button>
            <Button
              w="48%"
              color={primaryColor}
              onClick={() => removeBookingMutation()}
              loading={isPending}
              data-testid="remove-modal-confirm-cta"
            >
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
